import { SortOptionTemplate } from "../components/Template/AssessmentTemplates";
import { Question } from "../components/Template/Modals/GenerateTemplateTestModal";
import { TemplateData } from "../Models";
import { APIURL, SectionGenerationData } from "./config";

export const fetchAssessmentTemplates = async () => {
  const res = await fetch(`${APIURL}/AssessmentTemplate`);
  const json = res.json();
  return json;
};

export const fetchAssessmentTemplatesWithQuestionnaires = async (
  sortOptions: SortOptionTemplate,
  newFilters: any,
  token: string,
) => {
  const res = await fetch(
    `${APIURL}/AssessmentTemplate/Questionnaires?sortField=${sortOptions.field}&sortDirection=${sortOptions.direction}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newFilters),
    }
  );
  const json = res.json();
  return json;
};

export const postAssessmentTemplate = async (
  assessmentTemplate: TemplateData,
  token: string
) => {
  const res = await fetch(`${APIURL}/AssessmentTemplate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(assessmentTemplate),
  });
  const json = res.json();
  return json;
};

export const getAssessmentTemplateThreshold = async (
  templateId: string | undefined,
  numberOfTimesAnswered: number | undefined
) => {
  if (templateId) {
    const res = await fetch(
      `${APIURL}/AssessmentTemplate/generate/${templateId}?numberOfTimesAnswered=${numberOfTimesAnswered}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const json = res.json();
    return json;
  }
};

export const generateAssessmentTemplateTests = async (
  numOfQuestions: string | number,
  numberOfTests: number | string,
  numberOfTimesAnswered: number | string,
  updatedSkillMappings: any,
  totalAssessmentTime: number | string,
  breakAfterNoQuestions: number,
  breakTime: number,
  token: string,
) => {
  if (updatedSkillMappings) {
    const res = await fetch(
      `${APIURL}/AssessmentTemplate/Generate/?noOfTests=${numberOfTests}&numOfQuestions=${numOfQuestions}&numberOfTimesAnswered=${numberOfTimesAnswered}&totalAssessmentTime=${totalAssessmentTime}&breakAfterNoQuestions=${breakAfterNoQuestions}&breakTime=${breakTime}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedSkillMappings),
      }
    );
    const json = res.json();
    return json;
  }
};

export const generateSectionedAssessmentTemplateTests = async (
  numberOfTests: number | string,
  totalAssessmentTime: number | string,
  breakTime: number,
  sections: SectionGenerationData[],
  token: string
) => {
  if (sections && sections.length > 0) {
    const requestBody = {
      noOfTests: Number(numberOfTests),
      totalAssessmentTime: Number(totalAssessmentTime),
      breakTime: breakTime,
      sections: sections
    };

    const res = await fetch(
      `${APIURL}/AssessmentTemplate/GenerateSectioned`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      }
    );
    const json = res.json();
    return json;
  }
};

export const postTemplateSkillMapping = async (
  templateId: string | undefined,
  skillMappings: {
    skillId: string;
    difficultyMin: number;
    difficultyMax: number;
    variationMin: number;
    variationMax: number;
    questionPercentage: number;
  }[],
  token: string
) => {
  if (templateId) {
    const res = await fetch(
      `${APIURL}/AssessmentTemplate/TempalteSkillMapping/${templateId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(skillMappings),
      }
    );
    const json = res.json();
    return json;
  }
};

export const updateTemplate = async (
  templateData: TemplateData,
  templateId: string | undefined,
  token: string
) => {
  const template = {
    id: templateId,
    name: templateData.name,
    description: templateData.description,
    time: templateData.time,
    positivePoints: templateData.positivePoints,
    negativePoints: templateData.negativePoints,
  };
  await fetch(`${APIURL}/AssessmentTemplate`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(template),
  });
  return;
};

export const deleteTemplate = async (id: string, token: string) => {
  await fetch(`${APIURL}/AssessmentTemplate/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return;
};

export const postAssessmentTemplateTests = async (
  assessments: Question[][] | undefined,
  templateId: string | undefined,
  baseScore: string,
  time: string | number,
  isDividedEq: boolean,
  token: string,
  exceptionalScore?: string | number
) => {
  // Appended only when supplied: the parameter is optional server-side, and sending an empty
  // value would bind as 0 rather than "not set".
  const exceptionalScoreParam =
    exceptionalScore === undefined || exceptionalScore === null || exceptionalScore === ""
      ? ""
      : `&exceptionalScore=${exceptionalScore}`;

  await fetch(
    `${APIURL}/AssessmentTemplate/AssignAssessments/${templateId}?baseScore=${baseScore}&time=${time}&isDividedEq=${isDividedEq}${exceptionalScoreParam}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(assessments),
    }
  );
  return;
};

export const createAssessmentCopy = async (
  assessmentId: string,
  templateId: string | undefined,
  minAccuracy: number,
  maxAccuracy: number,
  minVariation: number,
  maxVariation: number
) => {
  const res = await fetch(
    `${APIURL}/AssessmentTemplate/generate/Test/Copy/${assessmentId}?templateId=${templateId}&minAccuracy=${minAccuracy}&MaxAccuracy=${maxAccuracy}&minVariation=${minVariation}&maxVariation=${maxVariation}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  const json = res.json();
  return json;
};

export const getAllQuestionsFromTemplate = async (tId: string, token: string) => {
  const res = await fetch(`${APIURL}/AssessmentTemplate/${tId}/questions`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await res.json();
  return json;
};

export const getTemplateSkillMappings = async (
  templateId: string | undefined,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/AssessmentTemplate/SkillMappings/${templateId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }
  );
  const json = res.json();
  return json;
};

export const deleteMultipleTemplates = async (templateIds: string[], token: string) => {
  await fetch(`${APIURL}/AssessmentTemplate/Multiple`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(templateIds),
  });
};


export const GenerateAndRecalibrateTemplate = async (
  templateId: string,
  updatedSkillMappings: any,
  assessmentQuestions: any,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/AssessmentTemplate/Generate/Recalibrate/${templateId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        generatedAssessmentData: assessmentQuestions,
        skillMappings: updatedSkillMappings,
      }),
    }
  );
  const json = await res.json();
  return json;
};

export const recalibrateTemplate = async (
  prevTempId: string,
  assessmentTemplate: TemplateData,
  assessmentData: Question[][] | undefined,
  skillMappings: {
    skillId: string;
    difficultyMin: number;
    difficultyMax: number;
    variationMin: number;
    variationMax: number;
    questionPercentage: number;
  }[],
  baseScore: string,
  token: string
) => {
  await fetch(`${APIURL}/AssessmentTemplate/Recalibrate/${prevTempId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      assessmentTemplate,
      assessmentData,
      skillMappings,
      baseScore,
    }),
  });
  return;
};

export const UpdateLog = async (templateId: string, token: string) => {
  await fetch(`${APIURL}/AssessmentTemplate/${templateId}/UpdateLog`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return;
};

export const getTemplateLogs = async (token: string) => {
  const res = await fetch(`${APIURL}/AssessmentTemplate/Logs`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
};
export const restoreTemplate = async (id: string, token: string) => {
  await fetch(`${APIURL}/AssessmentTemplate/${id}/Restore`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return;
};

export const fetchAssessmentQuestionnairesByTemplate = async (
  templateId: string,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/AssessmentTemplate/Questionnaires/${templateId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.json();
};

export const recalculateOutOfBounds = async (token: string) => {
  const url = `${APIURL}/AssessmentTemplate/recalculate-out-of-bounds`;
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    console.error("Error updating metrics:", err);
  }
};
